import { debounce } from '@/lib/utils';

class PriceRangeSlider extends HTMLElement {
  private slider!: HTMLElement;
  private minHandle!: HTMLElement;
  private maxHandle!: HTMLElement;
  private rangeTrack!: HTMLElement;
  private minInput!: HTMLInputElement;
  private maxInput!: HTMLInputElement;
  private minDisplay!: HTMLElement;
  private maxDisplay!: HTMLElement;
  private minTooltip!: HTMLElement;
  private maxTooltip!: HTMLElement;
  private minTooltipValue!: HTMLElement;
  private maxTooltipValue!: HTMLElement;
  private isDragging = false;
  private activeHandle: HTMLElement | null = null;
  private min!: number;
  private max!: number;
  private lastValidMin!: number;
  private lastValidMax!: number;

  constructor() {
    super();
    this.init();
  }

  private init() {
    this.slider = this.querySelector('.dual-range-slider') as HTMLElement;
    this.minHandle = this.querySelector('.range-handle-min') as HTMLElement;
    this.maxHandle = this.querySelector('.range-handle-max') as HTMLElement;
    this.rangeTrack = this.querySelector('.range-track') as HTMLElement;
    this.minInput = document.getElementById('price-range-min') as HTMLInputElement;
    this.maxInput = document.getElementById('price-range-max') as HTMLInputElement;
    this.minDisplay = this.querySelector('.range-display-min') as HTMLElement;
    this.maxDisplay = this.querySelector('.range-display-max') as HTMLElement;
    this.minTooltip = this.querySelector('.range-tooltip-min') as HTMLElement;
    this.maxTooltip = this.querySelector('.range-tooltip-max') as HTMLElement;
    this.minTooltipValue = this.minTooltip?.querySelector('.range-tooltip-value') as HTMLElement;
    this.maxTooltipValue = this.maxTooltip?.querySelector('.range-tooltip-value') as HTMLElement;

    if (!this.slider || !this.minHandle || !this.maxHandle || !this.rangeTrack || !this.minInput || !this.maxInput) {
      return;
    }

    this.min = parseFloat(this.slider.dataset.min || '0');
    this.max = parseFloat(this.slider.dataset.max || '100');

    this.bindEvents();
    // Initialize last valid values based on current inputs or bounds
    const initialMin = parseFloat(this.minInput.value);
    const initialMax = parseFloat(this.maxInput.value);
    this.lastValidMin = Number.isFinite(initialMin) ? initialMin : this.min;
    this.lastValidMax = Number.isFinite(initialMax) ? initialMax : this.max;

    this.updateSliderFromInputs();
  }

  private bindEvents() {
    // Handle dragging
    this.minHandle.addEventListener('mousedown', (e) => this.startDrag(e, this.minHandle));
    this.maxHandle.addEventListener('mousedown', (e) => this.startDrag(e, this.maxHandle));

    // Touch events for mobile
    this.minHandle.addEventListener('touchstart', (e) => this.startDrag(e, this.minHandle));
    this.maxHandle.addEventListener('touchstart', (e) => this.startDrag(e, this.maxHandle));

    // Make tooltips always visible
    if (this.minTooltip) this.showTooltip(this.minTooltip);
    if (this.maxTooltip) this.showTooltip(this.maxTooltip);

    // Keyboard support
    this.minHandle.addEventListener('keydown', (e) => this.handleKeydown(e, this.minHandle));
    this.maxHandle.addEventListener('keydown', (e) => this.handleKeydown(e, this.maxHandle));

    // Input field changes
    this.minInput.addEventListener('input', this.debouncedInput);
    this.maxInput.addEventListener('input', this.debouncedInput);

    // Global mouse/touch events
    document.addEventListener('mousemove', (e) => this.drag(e));
    document.addEventListener('mouseup', () => this.endDrag());
    document.addEventListener('touchmove', (e) => this.drag(e));
    document.addEventListener('touchend', () => this.endDrag());
  }

  debouncedInput = debounce(() => {
    this.updateSliderFromInputs();
  }, 500);

  private startDrag(e: MouseEvent | TouchEvent, handle: HTMLElement) {
    e.preventDefault();
    this.isDragging = true;
    this.activeHandle = handle;
    handle.focus();
  }

  private drag(e: MouseEvent | TouchEvent) {
    if (!this.isDragging || !this.activeHandle) return;

    const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
    const rect = this.slider.getBoundingClientRect();
    const percentage = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const value = this.min + percentage * (this.max - this.min);

    if (this.activeHandle === this.minHandle) {
      const maxValue = parseFloat(this.maxInput.value) || this.max;
      const newMinValue = Math.min(value, maxValue);
      this.minInput.value = Math.round(newMinValue).toString();
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      const minValue = parseFloat(this.minInput.value) || this.min;
      const newMaxValue = Math.max(value, minValue);
      this.maxInput.value = Math.round(newMaxValue).toString();
      this.maxInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.updateSliderVisuals();
    this.updateTooltips();
  }

  private endDrag() {
    if (!this.isDragging) return;
    this.isDragging = false;
    const lastHandle = this.activeHandle;
    this.activeHandle = null;

    // If handles overlapped or hit the left boundary, restore to last valid values
    const minVal = Number.isFinite(parseFloat(this.minInput.value))
      ? parseFloat(this.minInput.value)
      : this.lastValidMin;
    const maxVal = Number.isFinite(parseFloat(this.maxInput.value))
      ? parseFloat(this.maxInput.value)
      : this.lastValidMax;

    if (lastHandle === this.maxHandle && maxVal <= minVal) {
      const fallbackMax = this.lastValidMax > minVal ? this.lastValidMax : this.max;
      this.maxInput.value = Math.round(fallbackMax).toString();
      this.updateSliderFromInputs();
    } else if (lastHandle === this.minHandle && minVal >= maxVal) {
      const fallbackMin = this.lastValidMin < maxVal ? this.lastValidMin : this.min;
      this.minInput.value = Math.round(fallbackMin).toString();
      this.updateSliderFromInputs();
    }
  }

  private handleKeydown(e: KeyboardEvent, handle: HTMLElement) {
    const step = e.shiftKey ? 10 : 1;
    let currentValue: number;
    let input: HTMLInputElement;

    if (handle === this.minHandle) {
      currentValue = parseFloat(this.minInput.value) || this.min;
      input = this.minInput;
    } else {
      currentValue = parseFloat(this.maxInput.value) || this.max;
      input = this.maxInput;
    }

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        input.value = Math.max(currentValue - step, this.min).toString();
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        input.value = Math.min(currentValue + step, this.max).toString();
        break;
      case 'Home':
        e.preventDefault();
        input.value = this.min.toString();
        break;
      case 'End':
        e.preventDefault();
        input.value = this.max.toString();
        break;
      default:
        return;
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.updateSliderVisuals();
    this.updateTooltips();
  }

  private updateSliderFromInputs() {
    this.updateSliderVisuals();
    this.updateTooltips();
  }

  private updateSliderVisuals() {
    // Clamp within [min,max] (min can be 0 per Shopify filters)
    const rawMin = parseFloat(this.minInput.value);
    const rawMax = parseFloat(this.maxInput.value);
    const effectiveMin = Number.isFinite(rawMin)
      ? rawMin
      : Number.isFinite(this.lastValidMin)
        ? this.lastValidMin
        : this.min;
    const effectiveMax = Number.isFinite(rawMax)
      ? rawMax
      : Number.isFinite(this.lastValidMax)
        ? this.lastValidMax
        : this.max;
    const clampedMin = Math.max(effectiveMin, this.min);
    const clampedMax = Math.max(effectiveMax, clampedMin);

    // Persist clamped values back to inputs to keep state consistent
    this.minInput.value = Math.min(clampedMin, this.max).toString();
    this.maxInput.value = Math.min(clampedMax, this.max).toString();

    // Track last valid values for bounce-back behavior
    this.lastValidMin = parseFloat(this.minInput.value);
    this.lastValidMax = parseFloat(this.maxInput.value);

    const minValue = parseFloat(this.minInput.value) || this.min;
    const maxValue = parseFloat(this.maxInput.value) || this.max;

    const minPercentage = ((minValue - this.min) / (this.max - this.min)) * 100;
    const maxPercentage = ((maxValue - this.min) / (this.max - this.min)) * 100;

    // Remove edge offsets to avoid jitter and cross-handle nudging
    this.minHandle.style.left = `${minPercentage}%`;
    this.maxHandle.style.left = `${maxPercentage}%`;

    // Update active track (no offset needed for track)
    this.rangeTrack.style.left = `${minPercentage}%`;
    this.rangeTrack.style.right = `${100 - maxPercentage}%`;

    // Update ARIA values
    this.minHandle.setAttribute('aria-valuenow', minValue.toString());
    this.maxHandle.setAttribute('aria-valuenow', maxValue.toString());

    // Update marker centering flags to avoid horizontal overflow near edges
    // If the handle is at least 10% away from the nearest edge, allow centered tooltip
    const EDGE_CENTER_THRESHOLD_PERCENT = 5;
    const minMarkerCenter = minPercentage >= EDGE_CENTER_THRESHOLD_PERCENT;
    const maxMarkerCenter = maxPercentage <= 100 - EDGE_CENTER_THRESHOLD_PERCENT;
    this.slider.setAttribute('data-min-marker-center', String(minMarkerCenter));
    this.slider.setAttribute('data-max-marker-center', String(maxMarkerCenter));
  }

  private updateTooltips() {
    const minValue = parseFloat(this.minInput.value) || this.min;
    const maxValue = parseFloat(this.maxInput.value) || this.max;

    if (this.minTooltipValue) {
      this.minTooltipValue.textContent = window.Shopify.formatMoney(minValue * 100);
    }
    if (this.maxTooltipValue) {
      this.maxTooltipValue.textContent = window.Shopify.formatMoney(maxValue * 100);
    }
  }

  private showTooltip(tooltip: HTMLElement) {
    if (tooltip) {
      tooltip.classList.remove('opacity-0');
      tooltip.classList.add('opacity-100');
    }
  }
}

// Register the custom element
if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRangeSlider);
}
